import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { usePasswordResetRequest } from "@orbit/api-client-react";
import { canonicalizePhone, formatPhDisplay } from "../lib/phone";
import { AuthLayout } from "../components/AuthLayout";

const RESET_SS_KEY = "orbit.passwordReset.attempt";

export interface ResetSessionState {
  resetToken: string;
  expiresAt: string;
  // Phase 32 (D104) — channel + masked target carried across to the
  // verify page so the "We texted ****567" / "We emailed m***a@x.com"
  // hint can be rendered without re-querying the server. The legacy
  // `phoneMasked` field is kept for SS hydration of older session
  // payloads written before D104 shipped.
  channel: "phone" | "email";
  targetMasked: string;
  phoneMasked?: string;
  // The user-supplied original (canonical phone OR raw email) — kept
  // around for diagnostics and a possible "resend" UX in the future.
  target: string;
}

export function persistResetSession(s: ResetSessionState) {
  try {
    sessionStorage.setItem(RESET_SS_KEY, JSON.stringify(s));
  } catch {
    // sessionStorage may be unavailable in private mode — fall through
    // and let the verify page render its "no active reset" branch.
  }
}

export function readResetSession(): ResetSessionState | null {
  try {
    const raw = sessionStorage.getItem(RESET_SS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ResetSessionState> & {
      phoneMasked?: string;
      phone?: string;
    };
    // Hydrate older (pre-D104) sessions that only have phoneMasked +
    // phone fields. They were always phone-channel.
    if (!parsed.channel) {
      return {
        resetToken: parsed.resetToken!,
        expiresAt: parsed.expiresAt!,
        channel: "phone",
        targetMasked: parsed.targetMasked ?? parsed.phoneMasked ?? "",
        target: parsed.target ?? parsed.phone ?? "",
      };
    }
    return parsed as ResetSessionState;
  } catch {
    return null;
  }
}

export function clearResetSession() {
  try {
    sessionStorage.removeItem(RESET_SS_KEY);
  } catch {
    // ignore
  }
}

type Channel = "phone" | "email";

export function ForgotPasswordPage() {
  const [, navigate] = useLocation();
  // Phase 32 (D104) — channel toggle. Phone is the default because
  // every Orbit customer has a phone (it's the primary identifier);
  // email is the second, optional recovery lane for customers who
  // added one. The server returns a uniform 200 either way so the
  // client never reveals whether a target is registered.
  //
  // Why we DON'T hide the Email option for emailSet:false customers
  // here: the page is unauthenticated — we have no `customer.me`
  // context yet, and probing emailSet by phone-lookup pre-login would
  // be a registration-enumeration vector (the whole reason the
  // /request endpoint returns a uniform response). Customers without
  // an email on file will receive a uniform "we sent it" response
  // and silently fall through to the verify step, which is a known
  // UX gap; follow-up #67 tracks adding a friendlier hint after the
  // user has typed a phone we recognize (which is no worse than the
  // phone-channel enumeration surface they already have).
  const [channel, setChannel] = useState<Channel>("phone");
  const [phoneRaw, setPhoneRaw] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const requestMut = usePasswordResetRequest();
  const canonical = useMemo(() => canonicalizePhone(phoneRaw), [phoneRaw]);

  // Loose client-side email check — server's EmailSchema is the
  // source of truth. We only need to keep an empty submit from
  // round-tripping a 422.
  const emailTrimmed = email.trim();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);

  const ready = channel === "phone" ? canonical !== null : emailOk;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (channel === "phone" && !canonical) {
      setError("Please enter a valid PH mobile number.");
      return;
    }
    if (channel === "email" && !emailOk) {
      setError("Please enter a valid email address.");
      return;
    }
    try {
      // D103/D104 — uniform-response privacy: the server returns the
      // same 200 envelope (with a synthetic resetToken/targetMasked)
      // whether the target is registered or not. We always navigate
      // forward; the verify step lands on ATTEMPT_NOT_FOUND for fakes.
      const r =
        channel === "phone"
          ? await requestMut.mutateAsync({ channel: "phone", phone: canonical! })
          : await requestMut.mutateAsync({ channel: "email", email: emailTrimmed });
      persistResetSession({
        resetToken: r.resetToken,
        expiresAt: r.expiresAt,
        channel: r.channel,
        targetMasked: r.targetMasked,
        target: channel === "phone" ? canonical! : emailTrimmed,
      });
      navigate("/forgot-password/verify");
    } catch (err) {
      const e = err as {
        status?: number;
        apiError?: { code?: string; message?: string };
      };
      if (e.status === 429) {
        setError("Too many attempts. Try again in a few minutes.");
      } else if (e.apiError?.code === "SMS_NOT_CONFIGURED") {
        setError(
          "SMS isn't available right now. Try email instead, or contact support.",
        );
      } else {
        setError(e.apiError?.message ?? "Couldn't send the code. Try again.");
      }
    }
  };

  const isPhone = channel === "phone";

  return (
    <AuthLayout
      title="Forgot password"
      subtitle={
        isPhone
          ? "We'll text a 6-digit code to your registered number."
          : "We'll email a 6-digit code to your registered address."
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {/* Channel toggle — radio-styled buttons keep keyboard a11y
            without pulling in a custom segmented-control component. */}
        <div
          className="flex rounded-md border border-ink-mid/40 p-0.5 text-sm"
          role="radiogroup"
          aria-label="Recovery channel"
        >
          <button
            type="button"
            role="radio"
            aria-checked={isPhone}
            onClick={() => setChannel("phone")}
            className={`flex-1 rounded px-3 py-2 font-medium transition ${
              isPhone ? "bg-navy text-white" : "text-ink-dark"
            }`}
            data-testid="reset-channel-phone"
          >
            Phone
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!isPhone}
            onClick={() => setChannel("email")}
            className={`flex-1 rounded px-3 py-2 font-medium transition ${
              !isPhone ? "bg-navy text-white" : "text-ink-dark"
            }`}
            data-testid="reset-channel-email"
          >
            Email
          </button>
        </div>

        {isPhone ? (
          <div>
            <label htmlFor="phone" className="label">Mobile number</label>
            <input
              id="phone"
              className="input"
              value={formatPhDisplay(phoneRaw)}
              onChange={(e) => setPhoneRaw(e.target.value)}
              placeholder="0917-123-4567"
              inputMode="tel"
              autoComplete="tel"
              required
            />
          </div>
        ) : (
          <div>
            <label htmlFor="email" className="label">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          </div>
        )}
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!ready || requestMut.isPending}
          data-testid="button-send-reset-code"
        >
          {requestMut.isPending ? "Sending…" : "Send code"}
        </button>
        <p className="text-center text-sm text-ink-dark">
          Remembered it?{" "}
          <a href="/login" className="underline text-navy">Back to sign in</a>
        </p>
      </form>
    </AuthLayout>
  );
}
